/** @file Popover shown when an arrow is selected — toggles per-scene visibility. */
import { appStore } from "../core/state.js";
import { execute } from "../core/history.js";
import {
  toggleArrowVisibleInSceneCommand,
  setArrowVisibleInAllScenesCommand,
  setArrowVisibleOnlyInActiveSceneCommand,
} from "../core/scene-commands.js";
import { h, clear } from "./dom.js";

/**
 * @param {HTMLElement} container
 * @param {{ store?: typeof appStore }} [deps]
 * @returns {() => void} unmount
 */
export function mountArrowScenePopover(container, deps = {}) {
  const store = deps.store ?? appStore;

  // Own sub-container so we never wipe siblings (header/toolbar/etc.) mounted on the same UI host.
  const host = document.createElement("div");
  host.className = "arrow-scene-popover-host";
  container.appendChild(host);

  function render() {
    const state = store.get();
    clear(host);
    if (!state.selectedDrawingId) {
      return;
    }
    const arrowId = state.selectedDrawingId;

    const panel = h(
      "div",
      {
        class:
          "arrow-scene-popover fixed right-4 top-20 bg-neutral-900 border border-neutral-700 rounded shadow-lg p-3 z-50 min-w-[220px]",
      },
      [
        h("h3", { class: "text-sm font-semibold text-white mb-2" }, [
          "Sichtbar in Szenen",
        ]),
      ],
    );

    const list = h("div", { class: "flex flex-col gap-1 max-h-64 overflow-y-auto" }, []);
    state.scenes.forEach((scene, idx) => {
      const checked = scene.visibleArrowIds.includes(arrowId);
      const label = h(
        "label",
        {
          class:
            "min-h-[44px] flex items-center gap-2 text-sm text-neutral-200 cursor-pointer px-2 hover:bg-neutral-800 rounded",
        },
        [],
      );
      const checkbox = /** @type {HTMLInputElement} */ (
        h("input", {
          type: "checkbox",
          class: "w-5 h-5",
          "data-scene-idx": String(idx),
        })
      );
      checkbox.checked = checked;
      checkbox.addEventListener("change", () => {
        const cmd = toggleArrowVisibleInSceneCommand(arrowId, idx);
        if (cmd) {
          execute(cmd);
        }
      });
      label.appendChild(checkbox);
      label.appendChild(document.createTextNode(`Scene ${idx + 1}`));
      list.appendChild(label);
    });
    panel.appendChild(list);

    const btnRow = h("div", { class: "flex gap-2 mt-3" }, []);
    const allBtn = h(
      "button",
      {
        type: "button",
        class:
          "arrow-scene-all flex-1 min-h-[44px] bg-emerald-700 hover:bg-emerald-600 text-white text-xs rounded px-2",
      },
      ["In allen Szenen"],
    );
    allBtn.addEventListener("click", () => {
      const cmd = setArrowVisibleInAllScenesCommand(arrowId);
      if (cmd) {
        execute(cmd);
      }
    });
    btnRow.appendChild(allBtn);

    const onlyBtn = h(
      "button",
      {
        type: "button",
        class:
          "arrow-scene-only flex-1 min-h-[44px] bg-amber-700 hover:bg-amber-600 text-white text-xs rounded px-2",
      },
      ["Nur aktuelle"],
    );
    onlyBtn.addEventListener("click", () => {
      const cmd = setArrowVisibleOnlyInActiveSceneCommand(arrowId);
      if (cmd) {
        execute(cmd);
      }
    });
    btnRow.appendChild(onlyBtn);
    panel.appendChild(btnRow);

    host.appendChild(panel);
  }

  render();
  const unsub = store.subscribe((next, prev) => {
    if (
      next.selectedDrawingId !== prev.selectedDrawingId ||
      next.scenes !== prev.scenes
    ) {
      render();
    }
  });

  return () => {
    unsub();
    host.remove();
  };
}
